import React, { useState, useEffect, useMemo } from 'react';
import BudgetSheetForm from './BudgetSheetForm';
import BudgetSheetPricing from './BudgetSheetPricing';
import budgetSheetService from '../services/budgetSheetService';
import surveyService from '../services/surveyService';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Plus, Trash2, Edit2, Eye, ChevronRight, ChevronDown, Home, CheckCircle, Calculator } from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const BudgetSheetsManager = ({ survey }) => {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedEnvironments, setExpandedEnvironments] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, sheetId: null, sheetName: '' });
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [pricingSheet, setPricingSheet] = useState(null);

  useEffect(() => {
    if (survey && survey.id) {
      loadData();
    }
  }, [survey?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sheetsResponse, surveyResponse] = await Promise.all([
        budgetSheetService.getSurveyBudgetSheets(survey.id),
        surveyService.getEnvironments(survey.id)
      ]);

      if (sheetsResponse.success) {
        setSheets(sheetsResponse.data);
      }
      if (surveyResponse.success) {
        setEnvironments(surveyResponse.environments || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const sheetsByEnvironment = useMemo(() => {
    const grouped = {};
    environments.forEach(env => {
      grouped[env.id] = {
        environment: env,
        sheets: sheets.filter(s => s.environment_id && Number(s.environment_id) === Number(env.id))
      };
    });
    sheets.filter(s => !s.environment_id).forEach(sheet => {
      if (!grouped['unassigned']) {
        grouped['unassigned'] = {
          environment: { id: null, environment_name: 'Sem Ambiente', environment_type: null },
          sheets: []
        };
      }
      grouped['unassigned'].sheets.push(sheet);
    });
    return grouped;
  }, [sheets, environments]);

  const toggleEnvironment = (envId) => {
    setExpandedEnvironments(prev => ({
      ...prev,
      [envId]: !prev[envId]
    }));
  };

  const handleNewSheet = (environmentId = null) => {
    setSelectedSheet(null);
    setSelectedEnvironmentId(environmentId);
    setShowForm(true);
  };

  const handleEditSheet = (sheet) => {
    setSelectedSheet(sheet);
    setSelectedEnvironmentId(sheet.environment_id);
    setShowForm(true);
  };

  const handleSaveSuccess = () => {
    setShowForm(false);
    setSelectedSheet(null);
    setSelectedEnvironmentId(null);
    loadData();
  };

  const handleDeleteSuccess = () => {
    setShowForm(false);
    setSelectedSheet(null);
    setSelectedEnvironmentId(null);
    loadData();
  };

  const handleDeleteClick = (sheet) => {
    setDeleteConfirm({ show: true, sheetId: sheet.id, sheetName: sheet.ambiente_tipo || 'Ficha' });
  };

  const handleDeleteConfirm = async () => {
    try {
      await budgetSheetService.deleteBudgetSheet(survey.id, deleteConfirm.sheetId);
      toast.success('Ficha excluída com sucesso!');
      setDeleteConfirm({ show: false, sheetId: null, sheetName: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir ficha');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, sheetId: null, sheetName: '' });
  };

  const handleFinalizeClick = () => {
    setFinalizeConfirm(true);
  };

  const handleFinalizeConfirm = async () => {
    try {
      await surveyService.finalizePresentation(survey.id);
      toast.success('Apresentação finalizada com sucesso!');
      setFinalizeConfirm(false);
      if (survey?.onStatusChange) {
        survey.onStatusChange('APRESENTADO_CLIENTE');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao finalizar apresentação');
    }
  };

  const handleFinalizeCancel = () => {
    setFinalizeConfirm(false);
  };

  const getModelTypeLabel = (type) => {
    return type === 'SOCIAL' ? 'Área Social' : 'Área de Serviço';
  };

  const getModelTypeColor = (type) => {
    return type === 'SOCIAL'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-green-100 text-green-800';
  };

  const getEnvironmentIcon = (type) => {
    return <Home className="w-4 h-4" />;
  };

  if (showForm) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
        <BudgetSheetForm
          surveyId={survey.id}
          environmentId={selectedEnvironmentId}
          environment={selectedEnvironmentId ? environments.find(e => e.id === selectedEnvironmentId) : null}
          existingSheet={selectedSheet}
          onSave={handleSaveSuccess}
          onCancel={() => setShowForm(false)}
          onDelete={handleDeleteSuccess}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Fichas de Orçamento
          </h3>
          <span className="text-sm text-gray-500">
            ({sheets.length} {sheets.length === 1 ? 'ficha' : 'fichas'})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            onChange={(e) => handleNewSheet(e.target.value ? parseInt(e.target.value) : null)}
            value=""
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">+ Nova Ficha (sem ambiente)</option>
            {environments.map(env => (
              <option key={env.id} value={env.id}>
                + Nova Ficha para: {env.environment_name}
              </option>
            ))}
          </select>
          <button
            onClick={handleFinalizeClick}
            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-1"
          >
            <CheckCircle className="w-4 h-4" />
            Finalizar Apresentação
          </button>
        </div>
      </div>

      {/* Lista de Fichas por Ambiente */}
      <div className="p-4">
        {loading ? (
          <p className="text-center text-gray-500 py-4">Carregando...</p>
        ) : Object.keys(sheetsByEnvironment).length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma ficha de orçamento cadastrada</p>
            <p className="text-sm text-gray-400 mt-1">
              Clique em "Nova Ficha" para adicionar
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(sheetsByEnvironment).map(([envId, { environment, sheets: envSheets }]) => (
              <div key={envId} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleEnvironment(envId)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    {getEnvironmentIcon(environment.environment_type)}
                    <span className="font-medium text-gray-900">
                      {environment.environment_name}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({envSheets.length} {envSheets.length === 1 ? 'ficha' : 'fichas'})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!envId && envSheets.length === 0 && (
                      <span
                        onClick={(e) => { e.stopPropagation(); handleNewSheet(null); }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer"
                      >
                        + Adicionar
                      </span>
                    )}
                    {ChevronDown ? (
                      expandedEnvironments[envId] !== false && envSheets.length > 0 ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )
                    ) : null}
                  </div>
                </button>

                {(expandedEnvironments[envId] !== false || envId === 'unassigned') && envSheets.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {envSheets.map((sheet) => (
                      <div key={sheet.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${getModelTypeColor(sheet.model_type)}`}>
                                  {getModelTypeLabel(sheet.model_type)}
                                </span>
                                {sheet.model_name && (
                                  <span className="text-sm text-gray-600">
                                    {sheet.model_name}
                                  </span>
                                )}
                              </div>
                              {sheet.created_at && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Criado em: {new Date(sheet.created_at).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {survey?.status === 'APRESENTADO_CLIENTE' && user?.role === 'GERENCIA' && (
                              <button
                                onClick={() => setPricingSheet(sheet)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                                title="Precificar"
                              >
                                <Calculator className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEditSheet(sheet)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(sheet)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Resumo dos dados */}
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          {sheet.cor_interna && (
                            <div>
                              <span className="text-gray-500">Cor Interna:</span>
                              <span className="ml-1 text-gray-700">{sheet.cor_interna}</span>
                            </div>
                          )}
                          {sheet.cor_externa && (
                            <div>
                              <span className="text-gray-500">Cor Externa:</span>
                              <span className="ml-1 text-gray-700">{sheet.cor_externa}</span>
                            </div>
                          )}
                          {sheet.modelo_puxador && (
                            <div>
                              <span className="text-gray-500">Puxador:</span>
                              <span className="ml-1 text-gray-700">{sheet.modelo_puxador}</span>
                            </div>
                          )}
                          {sheet.modelo_corpo && (
                            <div>
                              <span className="text-gray-500">Corpo:</span>
                              <span className="ml-1 text-gray-700">{sheet.modelo_corpo}</span>
                            </div>
                          )}
                          {parseFloat(sheet.calculated_total || 0) > 0 && (
                            <div>
                              <span className="text-gray-500">Total Calculado:</span>
                              <span className="ml-1 font-bold text-emerald-700">
                                {formatCurrency(sheet.calculated_total)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Excluir Ficha</h3>
                <p className="text-sm text-gray-500">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir a ficha <strong>{deleteConfirm.sheetName}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Presentation Confirmation Modal */}
      {finalizeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Finalizar Apresentação</h3>
                <p className="text-sm text-gray-500">Mudar status para Apresentado ao Cliente</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Após finalizar, o levantamento será transferido para a GERÊNCIA e ficará aguardando orçamento. Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleFinalizeCancel}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleFinalizeConfirm}
                className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg"
              >
                Finalizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Panel Modal */}
      {pricingSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Precificação
              </h3>
              <button
                onClick={() => setPricingSheet(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-4">
              <BudgetSheetPricing
                survey={survey}
                budgetSheet={pricingSheet}
                onCalculated={(result) => {
                  setSheets(prev => prev.map(s => 
                    s.id === pricingSheet.id 
                      ? { ...s, calculated_total: result.calculated_total, price_calculated: true }
                      : s
                  ));
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetSheetsManager;
